// A data channel over one iroh (QUIC) stream. Streams carry bytes, so each frame (framing.ts)
// is length-prefixed, and the stream starts with a header naming the channel, which WebRTC
// negotiates in band for data channels:
//
//   header = [u8 version=1][u16 labelLen][label][u16 protocolLen][protocol]
//   then    [u32 frameLen][frame] …
//
// Compatibility: the header version and the connection's ALPN ("codeshare/room/1", in
// packages/iroh/rust) change together when this format does.

import type { IrohStream } from "@codeshare/iroh";
import type { DataChannelLike } from "./channel";
import { MAX_FRAME_BYTES } from "./framing";

const HEADER_VERSION = 1;
const MAX_NAME_BYTES = 1024;
/** Read chunk size. */
const READ_BYTES = 64 * 1024;
const encoder = new TextEncoder();
const decoder = new TextDecoder("utf-8", { fatal: true });

export interface StreamHeader {
  label: string;
  protocol: string;
}

export function encodeStreamHeader(header: StreamHeader): Uint8Array<ArrayBuffer> {
  const label = encoder.encode(header.label);
  const protocol = encoder.encode(header.protocol);
  if (label.byteLength > MAX_NAME_BYTES || protocol.byteLength > MAX_NAME_BYTES) {
    throw new Error("channel name too long");
  }
  const out = new Uint8Array(1 + 2 + label.byteLength + 2 + protocol.byteLength);
  const view = new DataView(out.buffer);
  out[0] = HEADER_VERSION;
  view.setUint16(1, label.byteLength);
  out.set(label, 3);
  view.setUint16(3 + label.byteLength, protocol.byteLength);
  out.set(protocol, 5 + label.byteLength);
  return out;
}

/** Parses a header from the start of `bytes`: the header and what follows it, or undefined if
 * more bytes are needed. Throws on a malformed header. */
export function decodeStreamHeader(
  bytes: Uint8Array,
): { header: StreamHeader; rest: Uint8Array } | undefined {
  if (bytes.byteLength < 3) return undefined;
  if (bytes[0] !== HEADER_VERSION) throw new Error(`unknown stream header version ${bytes[0]}`);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const labelLength = view.getUint16(1);
  if (labelLength > MAX_NAME_BYTES) throw new Error("channel label too long");
  if (bytes.byteLength < 5 + labelLength) return undefined;
  const protocolLength = view.getUint16(3 + labelLength);
  if (protocolLength > MAX_NAME_BYTES) throw new Error("channel protocol too long");
  const end = 5 + labelLength + protocolLength;
  if (bytes.byteLength < end) return undefined;
  return {
    header: {
      label: decoder.decode(bytes.subarray(3, 3 + labelLength)),
      protocol: decoder.decode(bytes.subarray(5 + labelLength, end)),
    },
    rest: bytes.subarray(end),
  };
}

function concat(a: Uint8Array, b: Uint8Array): Uint8Array {
  if (a.byteLength === 0) return b;
  const out = new Uint8Array(a.byteLength + b.byteLength);
  out.set(a);
  out.set(b, a.byteLength);
  return out;
}

/**
 * Adapts an iroh stream to the RTCDataChannel shape Channel builds on. Open as soon as it
 * exists (QUIC streams are), with the same backpressure signals (bufferedAmount and
 * `bufferedamountlow`).
 */
export class IrohDataChannel implements DataChannelLike {
  readonly label: string;
  readonly protocol: string;
  readyState: "connecting" | "open" | "closing" | "closed" = "open";
  bufferedAmount = 0;
  bufferedAmountLowThreshold = 0;
  binaryType: "blob" | "arraybuffer" = "arraybuffer";
  onopen: ((event: Event) => void) | null = null;
  onclose: ((event: Event) => void) | null = null;
  onerror: ((event: Event) => void) | null = null;
  #onmessage: ((event: MessageEvent) => void) | null = null;
  /**
   * Frames that arrived before anyone listened. An accepted stream can carry its first frames
   * right behind the header, before the Channel that will consume it is attached; unlike an
   * RTCDataChannel's event task, nothing else holds them back.
   */
  #early: MessageEvent[] = [];

  readonly #lowListeners = new Set<() => void>();
  readonly #queue: Uint8Array[] = [];
  #stream: IrohStream | undefined;
  #pumping = false;
  #finishing = false;

  private constructor(header: StreamHeader) {
    this.label = header.label;
    this.protocol = header.protocol;
  }

  /** A stream this side opens: the header goes out first. */
  static open(stream: Promise<IrohStream> | IrohStream, header: StreamHeader): IrohDataChannel {
    const channel = new IrohDataChannel(header);
    channel.#queue.push(encodeStreamHeader(header));
    Promise.resolve(stream).then(
      (s) => channel.#start(s, new Uint8Array(0)),
      (error) => channel.#fail(error),
    );
    return channel;
  }

  /** A stream the other side opened: reads the header, then hands over a ready channel. */
  static async accept(stream: IrohStream): Promise<IrohDataChannel> {
    let buffered: Uint8Array = new Uint8Array(0);
    for (;;) {
      const parsed = decodeStreamHeader(buffered);
      if (parsed) {
        const channel = new IrohDataChannel(parsed.header);
        channel.#start(stream, parsed.rest);
        return channel;
      }
      const chunk = await stream.read(READ_BYTES);
      if (!chunk) throw new Error("stream ended before its header");
      buffered = concat(buffered, chunk);
      if (buffered.byteLength > 5 + 2 * MAX_NAME_BYTES) throw new Error("header too long");
    }
  }

  get onmessage(): ((event: MessageEvent) => void) | null {
    return this.#onmessage;
  }

  set onmessage(handler: ((event: MessageEvent) => void) | null) {
    this.#onmessage = handler;
    if (!handler) return;
    for (const event of this.#early.splice(0)) handler(event);
  }

  send(data: Uint8Array<ArrayBuffer>): void {
    if (this.readyState !== "open") throw new Error("channel is not open");
    if (data.byteLength > MAX_FRAME_BYTES) throw new Error("frame too large");
    const framed = new Uint8Array(4 + data.byteLength);
    new DataView(framed.buffer).setUint32(0, data.byteLength);
    framed.set(data, 4);
    this.#queue.push(framed);
    this.bufferedAmount += data.byteLength;
    void this.#pump();
  }

  close(): void {
    if (this.readyState === "closing" || this.readyState === "closed") return;
    this.readyState = "closing";
    this.#finishing = true;
    void this.#pump();
  }

  addEventListener(_type: "bufferedamountlow", listener: () => void): void {
    this.#lowListeners.add(listener);
  }

  removeEventListener(_type: "bufferedamountlow", listener: () => void): void {
    this.#lowListeners.delete(listener);
  }

  #start(stream: IrohStream, buffered: Uint8Array): void {
    if (this.readyState === "closed") {
      void stream.abort(0).catch(() => {});
      return;
    }
    this.#stream = stream;
    this.onopen?.(new Event("open"));
    void this.#pump();
    void this.#read(buffered);
  }

  async #pump(): Promise<void> {
    const stream = this.#stream;
    if (!stream || this.#pumping) return;
    this.#pumping = true;
    try {
      while (this.#queue.length > 0) {
        const bytes = this.#queue.shift() as Uint8Array;
        await stream.write(bytes);
        if (this.readyState === "closed") return;
        const payload = Math.max(0, bytes.byteLength - 4);
        const wasHigh = this.bufferedAmount > this.bufferedAmountLowThreshold;
        this.bufferedAmount = Math.max(0, this.bufferedAmount - payload);
        if (wasHigh && this.bufferedAmount <= this.bufferedAmountLowThreshold) {
          for (const listener of this.#lowListeners) listener();
        }
      }
      if (this.#finishing) {
        this.#finishing = false;
        await stream.finish();
        // Our side is done; don't wait for the other side's remaining bytes.
        await stream.abort(0).catch(() => {});
        this.#closed();
      }
    } catch (error) {
      this.#fail(error);
    } finally {
      this.#pumping = false;
    }
  }

  async #read(initial: Uint8Array): Promise<void> {
    const stream = this.#stream;
    if (!stream) return;
    let buffered: Uint8Array = initial;
    try {
      for (;;) {
        while (buffered.byteLength >= 4) {
          const length = new DataView(buffered.buffer, buffered.byteOffset).getUint32(0);
          if (length > MAX_FRAME_BYTES) throw new Error("frame too large");
          if (buffered.byteLength < 4 + length) break;
          const frame = new Uint8Array(buffered.slice(4, 4 + length));
          buffered = buffered.subarray(4 + length);
          if (this.readyState === "closed") return;
          const event = new MessageEvent("message", { data: frame });
          if (this.#onmessage) this.#onmessage(event);
          else this.#early.push(event);
        }
        const chunk = await stream.read(READ_BYTES);
        if (!chunk) {
          // The other side finished: close our side too (after what's queued).
          this.close();
          this.#closed();
          return;
        }
        buffered = concat(buffered, chunk);
      }
    } catch (error) {
      this.#fail(error);
    }
  }

  #fail(error: unknown): void {
    if (this.readyState === "closed") return;
    void this.#stream?.abort(1).catch(() => {});
    this.onerror?.(new ErrorEvent("error", { error, message: String(error) }) as never);
    this.#closed();
  }

  #closed(): void {
    if (this.readyState === "closed") return;
    this.readyState = "closed";
    this.#queue.length = 0;
    this.#early.length = 0;
    this.bufferedAmount = 0;
    this.onclose?.(new Event("close"));
  }
}
